import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Popover, Tooltip } from "antd";
import moment from "moment";

import "../../../components/commonComponents/calendar/overviewCalender.css";
import { ICONS } from "../../../assets/icons/index";
import CardContent from "./CardContent";
import EventPopover from "./EventPopover";

/**
 * Status codes used throughout the UI.
 */
const STATUS = {
  SCHEDULED: 10,
  RUNNING: 11,
  COMPLETED: 12,
};

/**
 * Format helpers
 */
const formatISTTime = (ts) => moment(ts).utcOffset("+05:30").format("hh:mm A");
const formatYMD = (ts) => moment(ts).format("YYYY-MM-DD");
const formatDMYDashed = (dateObj) =>
  `${dateObj.getDate()}-${dateObj.getMonth() + 1}-${dateObj.getFullYear()}`;

/**
 * Filter utility: returns true if the item's status passes the selectedStatus filter.
 */
const passesStatusFilter = (status, selectedStatus) =>
  selectedStatus?.length ? selectedStatus.includes(status) : true;

/**
 * BoardCards
 * - Month view expects `card` as an Array of broadcasts
 * - Week view expects `card` as an Object keyed by some slot, values are broadcasts
 */
const BoardCards = ({ card, pageNavigate, col, selectedStatus, events = [] }) => {
  const [showAll, setShowAll] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  /**
   * Per-broadcast Popover visibility map:
   *  { [broadcastId]: boolean }
   */
  const [popoverOpenMap, setPopoverOpenMap] = useState({});

  const toggleShowAll = () => setShowAll((s) => !s);

  const setPopoverOpen = useCallback((id, open) => {
    setPopoverOpenMap((prev) => ({ ...prev, [id]: open }));
  }, []);

  const closePopover = useCallback((id) => setPopoverOpen(id, false), [setPopoverOpen]);

  /**
   * In Week view, we need to know if multiple broadcasts share the exact same date+time,
   * so we can switch to a flex-row layout / overlapping cards style.
   */
  const shouldDisplayFlexRow = useMemo(() => {
    if (!card) return false;
    const broadcasts = pageNavigate === "Week" ? Object.values(card) : card;
    if (!Array.isArray(broadcasts) || broadcasts.length <= 1) return false;

    const byDateTime = broadcasts.reduce((acc, b) => {
      const when = b?.schedule_on;
      if (!when) return acc;
      const key = `${formatYMD(when)}-${moment(when).format("HH:mm")}`;
      (acc[key] ||= []).push(b);
      return acc;
    }, {});
    return Object.values(byDateTime).some((arr) => arr.length > 1);
  }, [card, pageNavigate]);

  /**
   * Month view: render up to 2 items, with "Show More/Less".
   */
  const renderMonth = () => {
    if (!Array.isArray(card)) return null;

    // Filter Google events matching this cell's date
    const googleEventsForCell = (events || []).filter((event) => {
      // Handle both structured and plain timestamps
      const startAt =
        event?.start_at?.dateTime || // preferred (Google Calendar style)
        event?.start_at ||           // fallback if string
        event?.created_at;           // fallback if no start_at

      if (!startAt) return false;

      // Convert to valid Date
      const eventDate = new Date(startAt);

      // Format using your utility (e.g., "YYYY-MM-DD")
      const formattedDate = formatDMYDashed(eventDate);

      // Compare with the current column date
      return col?.date === formattedDate;
    });

    return (
      <>
        {/* Broadcasts */}
        {card.slice(0, showAll ? card.length : 2).map((broadcast) => {
          // ... unchanged broadcast rendering ...
          // (same as before)
          const scheduleOn = new Date(broadcast?.schedule_on);
          const formattedDate = formatDMYDashed(scheduleOn);
          const matchThisCell = col?.date === formattedDate;

          const allowedStatus =
            broadcast?.status === STATUS.SCHEDULED ||
            broadcast?.status === STATUS.RUNNING ||
            broadcast?.status === STATUS.COMPLETED;

          if (!matchThisCell || broadcast?.status === 0 || !allowedStatus) return null;
          if (!passesStatusFilter(broadcast?.status, selectedStatus)) return null;

          return (
            <Popover
              key={broadcast?.id}
              content={
                <CardContent
                  handleClose={() => closePopover(broadcast?.id)}
                  broadcastList={broadcast}
                  col={col}
                />
              }
              trigger="click"
              open={!!popoverOpenMap[broadcast?.id]}
              onOpenChange={(open) => setPopoverOpen(broadcast?.id, open)}
              arrow={false}
            >
              <div
                className="boardCard p-5 pointer mb-5 flex-row"
                onClick={() => setHoveredCard(broadcast?.id)}
              >
                <div
                  style={{
                    borderLeft: broadcast?.display_color
                      ? `3px solid ${broadcast?.display_color}`
                      : undefined,
                    borderRadius: "2.5px",
                    marginLeft: "4px",
                  }}
                  className="flex pl-5"
                >
                  <div>
                    <div className="flex-row align-center">
                      {broadcast?.display_emoji && (
                        <p className="pr-1 text-[0.9vw]">{broadcast?.display_emoji}</p>
                      )}
                      <img src={ICONS?.whatsApp} className="boardCardWhatsapp" alt="WhatsApp" />
                      <p className="boardCardText">
                        {broadcast?.name
                          ? broadcast.name.charAt(0).toUpperCase() + broadcast.name.slice(1)
                          : ""}
                      </p>
                    </div>

                    <div className="w-[8.5vw] flex-row space-between mt-1">
                      {/* Status + time */}
                      <div className="flex-row align-center">
                        {broadcast?.status === STATUS.SCHEDULED && (
                          <Tooltip title={<span style={{ fontSize: 12 }}>Scheduled</span>} placement="right">
                            <div className="flex-row align-center">
                              <img src={ICONS?.calenderScheduled} className="w-[17px]" alt="Scheduled" />
                              <p className="boardTime">{formatISTTime(broadcast?.schedule_on)}</p>
                            </div>
                          </Tooltip>
                        )}

                        {broadcast?.status === STATUS.RUNNING && (
                          <>
                            <img src={ICONS?.CampaignRunningIcon} className="boardCardRunning" alt="Running" />
                            <p style={{ color: "#0ACD95" }} className="calenderIcons">Running</p>
                          </>
                        )}

                        {broadcast?.status === STATUS.COMPLETED && (
                          <div className="flex-row align-center">
                            <img src={ICONS?.CampaignCompleteIcon} className="w-[14px]" alt="Completed" />
                            <p className="calenderIcons" style={{ color: "#4C90F5" }}>Completed</p>
                          </div>
                        )}
                      </div>

                      {/* Files / Segments */}
                      <div className="flex-row align-center">
                        <div className="boardTime flex-row align-center">
                          <img src={ICONS?.uploadFile} alt="Files" />
                          <p className="boardSegment pr-5">
                            <span>{broadcast?.uploaded_files_count}</span>
                          </p>
                        </div>

                        <div className="boardTime flex-row align-center">
                          <img src={ICONS?.BroadcastSegmentIcons} className="w-[17px]" alt="Segments" />
                          <p className="boardSegment pr-5">
                            <span>{broadcast?.segments_count}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Popover>
          );
        })}

        {/* Google Events */}
        {googleEventsForCell.slice(0, showAll ? googleEventsForCell.length : 3).map((event) => (
          <div key={event.id} className="boardCard p-5 pointer mb-5 flex-row boardCard-google-event">
            <EventPopover event={event} />
          </div>
        ))}

        {card.length > 2 || googleEventsForCell.length > 3 && (
          <button onClick={toggleShowAll} className="p-relative flex-row align-center show-button">
            <img
              src={ICONS?.CalenderArrow}
              className={`boardCardArrow mr-1 ${showAll ? "rotate-180" : ""}`}
              alt="Toggle"
            />
            {showAll ? "Show Less" : "Show More"}
          </button>
        )}
      </>
    );
  };

  /**
   * Week view: show cards for the day column; supports overlapping/flex layout if times collide.
   */
  const renderWeek = () => {
    const broadcasts = Object.values(card || {});
    // Filter Google events for this day/slot (if col.date is provided)
    let googleEventsForCell = [];

    if (col?.date) {
      googleEventsForCell = (events || []).filter((event) => {
        // Safely extract the correct date source
        const startAt =
          event?.start_at?.dateTime ||
          event?.start_at ||
          event?.created_at;

        if (!startAt) return false;

        // Normalize date using time zone (if available)
        const eventDate = new Date(startAt);
        const formattedDate = formatDMYDashed(eventDate);

        return col.date === formattedDate;
      });
    }

    const length = broadcasts.length;

    return (
      <div className={`board-cards-container ${shouldDisplayFlexRow ? "flex-row" : ""}`}>
        {/* Broadcasts */}
        {broadcasts.map((broadcast) => {
          if (broadcast?.status === 0) return null;
          if (!passesStatusFilter(broadcast?.status, selectedStatus)) return null;

          return (
            <Popover
              key={broadcast?.id}
              content={
                <CardContent
                  handleClose={() => closePopover(broadcast?.id)}
                  broadcastList={broadcast}
                />
              }
              trigger="click"
              open={!!popoverOpenMap[broadcast?.id]}
              onOpenChange={(open) => setPopoverOpen(broadcast?.id, open)}
              arrow={false}
              placement="right"
            >
              <div
                className={[
                  "boardCard pt-5 pb-5 pl-0 pointer mb-5 flex-row p-relative",
                  shouldDisplayFlexRow ? "flex-card" : "",
                  shouldDisplayFlexRow && length > 2 ? "card-overlap" : "",
                  !shouldDisplayFlexRow && length > 1 ? "asc" : "",
                  hoveredCard === broadcast?.id ? "expanded" : "",
                  hoveredCard && hoveredCard !== broadcast?.id ? "hidden" : "",
                ].join(" ")}
                onMouseEnter={() => setHoveredCard(broadcast?.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div
                  style={{
                    borderLeft: broadcast?.display_color
                      ? `3px solid ${broadcast?.display_color}`
                      : undefined,
                    borderRadius: "2.5px",
                    marginLeft: "4px",
                  }}
                  className="flex pl-5"
                >
                  <div>
                    <div className="flex-row align-center">
                      {broadcast?.display_emoji && (
                        <p className="pr-1 text-[0.9vw]">{broadcast?.display_emoji}</p>
                      )}
                      <img src={ICONS?.whatsApp} className="boardCardWhatsapp" alt="WhatsApp" />
                      <p
                        className={`boardCardText ${hoveredCard === broadcast?.id
                            ? ""
                            : shouldDisplayFlexRow
                              ? "boardCardText-flex"
                              : ""
                          }`}
                      >
                        {broadcast?.name
                          ? broadcast.name.charAt(0).toUpperCase() + broadcast.name.slice(1)
                          : ""}
                      </p>
                    </div>

                    <div className="w-[9vw] flex-row mt-1 space-between">
                      {/* Status + time */}
                      <div className="flex-row align-center">
                        {broadcast?.status === STATUS.SCHEDULED && (
                          <Tooltip title={<span style={{ fontSize: 12 }}>Scheduled</span>} placement="right">
                            <div className="flex-row align-center">
                              <img src={ICONS?.calenderScheduled} className="w-[17px]" alt="Scheduled" />
                              <p className="boardTime">{formatISTTime(broadcast?.schedule_on)}</p>
                            </div>
                          </Tooltip>
                        )}

                        {broadcast?.status === STATUS.RUNNING && (
                          <>
                            <img src={ICONS?.CampaignRunningIcon} className="boardCardRunning" alt="Running" />
                            <p style={{ color: "#0ACD95" }} className="calenderIcons">Running</p>
                          </>
                        )}

                        {broadcast?.status === STATUS.COMPLETED && (
                          <div className="flex-row items-center">
                            <img src={ICONS?.CampaignCompleteIcon} className="w-[14px]" alt="Completed" />
                            <p className="calenderIcons" style={{ color: "#4C90F5" }}>Completed</p>
                          </div>
                        )}
                      </div>

                      {/* Files / Segments */}
                      <div className="flex-row align-center">
                        <div className="boardTime flex-row align-center">
                          <img src={ICONS?.uploadFile} alt="Files" />
                          <p className="boardSegment">
                            <span>{broadcast?.uploaded_files_count}</span>
                          </p>
                        </div>

                        <div className="boardTime flex-row align-center">
                          <img src={ICONS?.BroadcastSegmentIcons} className="w-[17px]" alt="Segments" />
                          <p className="boardSegment">
                            <span>{broadcast?.segments_count}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Popover>
          );
        })}
        {/* Google Events */}
        {googleEventsForCell.map((event) => (
          <div key={event.id} className="boardCard p-5 pointer mb-5 flex-row boardCard-google-event">
            <EventPopover event={event} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      {pageNavigate === "Month" && renderMonth()}
      {pageNavigate === "Week" && renderWeek()}
    </div>
  );
};

BoardCards.propTypes = {
  /**
   * For "Month": Array of broadcasts.
   * For "Week": Object with broadcasts as values.
   * Each broadcast example:
   * {
   *   id: number|string,
   *   name: string,
   *   status: 10|11|12|0,
   *   schedule_on: string|Date,
   *   display_color?: string,
   *   display_emoji?: string,
   *   uploaded_files_count?: number,
   *   segments_count?: number
   * }
   */
  card: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  /**
   * "Month" or "Week"
   */
  pageNavigate: PropTypes.string.isRequired,
  /**
   * Month cell meta; expects { date: "DD-M-YYYY" } in Month view
   */
  col: PropTypes.shape({
    date: PropTypes.string,
  }),
  /**
   * Status filter array, e.g. [10, 11]
   */
  selectedStatus: PropTypes.arrayOf(PropTypes.number),
  /**
   * Google events array
   */
  events: PropTypes.array,
};

BoardCards.defaultProps = {
  card: [],
  col: null,
  selectedStatus: [],
  events: [],
};

export default BoardCards;
